import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Users, Building, Phone, Mail, CheckCircle, XCircle, 
  Calendar, Euro, ChevronRight
} from 'lucide-react';
import { Button } from '../../../components/ui/button';

import { API_URL } from '../../../config/api';

const subscriptionLabels = {
  active: { label: 'Actif', color: 'bg-green-100 text-green-600' },
  cancelled: { label: 'Annulé', color: 'bg-red-100 text-red-600' },
  expired: { label: 'Expiré', color: 'bg-amber-100 text-amber-600' },
  trial: { label: 'Essai', color: 'bg-blue-100 text-blue-600' }
};

export default function AdminClientsPage() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('');

  useEffect(() => {
    fetchClients();
  }, [filterStatus]);

  const fetchClients = async () => {
    const token = localStorage.getItem('admin_token');
    if (!token) return;

    try {
      let url = `${API_URL}/api/admin-crm/clients`;
      if (filterStatus) url += `?status=${filterStatus}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        setClients(await response.json());
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  // Calculate totals
  const totalMRR = clients.reduce((sum, c) => {
    if (c.subscription_status !== 'active') return sum;
    const prices = { essentiel: 499, business: 949, premium: 1499 };
    return sum + (prices[c.current_offer_name?.toLowerCase()] || 0);
  }, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-clients-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Clients</h1>
          <p className="text-gray-500">Abonnés BatiAssist</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-sm text-gray-500">Total clients</p>
          <p className="text-2xl font-bold text-gray-900">{clients.length}</p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-sm text-green-600">Actifs</p>
          <p className="text-2xl font-bold text-green-600">
            {clients.filter(c => c.subscription_status === 'active').length}
          </p>
        </div>
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
          <p className="text-sm text-purple-600">MRR</p>
          <p className="text-2xl font-bold text-purple-600">{formatCurrency(totalMRR)}</p>
        </div>
      </div>

      {/* Filter */}
      <div className="flex gap-4">
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg"
        >
          <option value="">Tous les statuts</option>
          {Object.entries(subscriptionLabels).map(([value, { label }]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      {/* Clients List */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {clients.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">Aucun client</p>
            <p className="text-sm text-gray-400 mt-1">Les clients sont créés quand un devis est accepté</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {clients.map((client) => {
              const status = subscriptionLabels[client.subscription_status] || subscriptionLabels.active;

              return (
                <div
                  key={client.id}
                  onClick={() => navigate(`/admin/crm/clients/${client.id}`)}
                  className="p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 font-semibold text-lg">{client.name?.charAt(0)}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-gray-900">{client.name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${status.color}`}>
                          {status.label}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        {client.company_name && (
                          <span className="flex items-center gap-1">
                            <Building className="w-3 h-3" />
                            {client.company_name}
                          </span>
                        )}
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" />
                          {client.email}
                        </span>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {client.current_offer_name && (
                        <span className="text-sm font-semibold text-purple-600">{client.current_offer_name}</span>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        Depuis {formatDate(client.subscription_start_date)}
                      </p>
                      {client.total_paid > 0 && (
                        <p className="text-xs text-green-600">
                          {formatCurrency(client.total_paid)} payés
                        </p>
                      )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
